import { useState } from 'react';
import { useSelector } from 'react-redux';
import { FaDownload } from 'react-icons/fa';
import styled from 'styled-components';

import { Input } from '../../../components/ui';
import {
  downloadInvoicePdf,
  getPreviousInvoices,
} from '../../../api/services/orderService';

const Title = styled.h1`
  width: 100%;
  font-size: 24px;
  margin-bottom: 1.5rem;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 20px;
    margin-bottom: 1rem;
  }
`;

const InputGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin: 2rem 0;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const InputLabel = styled.label`
  font-size: 14px;
  color: #4a4a4a;
`;

const InputContainer = styled.div`
  border: 1px solid #ddd;
  width: 100%;
  box-sizing: border-box;
`;

const SearchButton = styled.button`
  width: 100%;
  padding: 12px;
  margin-bottom: 2rem;
  background: ${(props) => props.theme.colors.primaryBlue};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  font-size: 16px;

  &:hover {
    background: #2b2370;
  }
`;

const InvoiceListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InvoiceListItem = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 1.4fr 1fr;
  gap: 1.5rem;
  align-items: center;
  border: 1px solid #eee;
  border-radius: 4px;
  padding: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const InvoiceNumber = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.primaryText};
`;

const MutedText = styled.div`
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
`;

const AmountText = styled.div`
  font-size: 18px;
  font-weight: 700;
  text-align: right;
  color: ${(props) => props.theme.colors.primaryBlue};

  @media (max-width: 768px) {
    text-align: left;
  }
`;

const AmountSection = styled(Section)`
  align-items: flex-end;

  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;

const DownloadButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  min-width: 150px;
  padding: 10px 14px;
  color: white;
  background: ${(props) => props.theme.colors.primaryBlue};
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;

  &:hover:not(:disabled) {
    background: #2b2370;
  }

  &:disabled {
    cursor: wait;
    opacity: 0.65;
  }
`;

const DownloadError = styled.div`
  margin: 1rem 0;
  padding: 0.75rem 1rem;
  color: #a12622;
  background: #fff4f3;
  border: 1px solid #f0c3bf;
  border-radius: 4px;
  font-size: 14px;
`;

const NoInvoicesMessage = styled.div`
  text-align: center;
  color: ${(props) => props.theme.colors.secondaryText};
  font-size: 16px;
  padding: 2rem;
  border: 1px solid #eee;
  border-radius: 4px;
`;

const formatDate = (dateValue) => {
  if (!dateValue) {
    return '-';
  }

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return dateValue;
  }

  return date.toLocaleDateString('en-US');
};

const formatAmount = (amountValue, currencyCode) => {
  const amount = Number(amountValue);
  if (Number.isNaN(amount)) {
    return amountValue || '-';
  }

  return `${currencyCode || 'USD'} ${amount.toFixed(2)}`;
};

const PreviousInvoiceView = () => {
  const userInfo = useSelector((state) => state.userInfo);
  const [invoiceList, setInvoiceList] = useState([]);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);
  const [downloadError, setDownloadError] = useState('');
  const [filter, setFilter] = useState({
    from: '',
    to: '',
    invoiceNo: '',
    orderNo: '',
    address: '',
  });

  const getDbInvoiceList = async () => {
    const result = await getPreviousInvoices(userInfo.idCustomer, filter);
    setInvoiceList(result.invoiceList || []);
  };

  const handleDownload = async (invoiceNumber) => {
    setDownloadingInvoice(invoiceNumber);
    setDownloadError('');

    try {
      const pdfBlob = await downloadInvoicePdf(
        userInfo.idCustomer,
        invoiceNumber,
      );
      const downloadUrl = window.URL.createObjectURL(pdfBlob);
      const downloadLink = document.createElement('a');
      downloadLink.href = downloadUrl;
      downloadLink.download = `invoice-${invoiceNumber}.pdf`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      downloadLink.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      let errorMessage =
        'Unable to download this invoice PDF. Please try again.';
      const responseData = error.response?.data;

      if (responseData instanceof Blob) {
        try {
          const errorBody = JSON.parse(await responseData.text());
          errorMessage = errorBody.message || errorMessage;
        } catch {
          // Keep the generic message when the response is not JSON.
        }
      } else if (responseData?.message) {
        errorMessage = responseData.message;
      }

      setDownloadError(errorMessage);
    } finally {
      setDownloadingInvoice(null);
    }
  };

  return (
    <>
      <Title>Search Previous Invoices</Title>

      <InputGrid>
        <InputGroup>
          <InputLabel>From Date</InputLabel>
          <InputContainer>
            <Input
              type='date'
              placeholder='month/day/year'
              value={filter.from}
              onChange={(value) => {
                setFilter({ ...filter, from: value });
              }}
            />
          </InputContainer>
        </InputGroup>

        <InputGroup>
          <InputLabel>To Date</InputLabel>
          <InputContainer>
            <Input
              type='date'
              placeholder='month/day/year'
              value={filter.to}
              onChange={(value) => {
                setFilter({ ...filter, to: value });
              }}
            />
          </InputContainer>
        </InputGroup>

        <InputGroup>
          <InputLabel>Invoice Number</InputLabel>
          <InputContainer>
            <Input
              placeholder='Enter Invoice Number'
              value={filter.invoiceNo}
              onChange={(value) => {
                setFilter({ ...filter, invoiceNo: value });
              }}
            />
          </InputContainer>
        </InputGroup>

        <InputGroup>
          <InputLabel>Purchase Order Number</InputLabel>
          <InputContainer>
            <Input
              placeholder='Enter Purchase Order Number'
              value={filter.orderNo}
              onChange={(value) => {
                setFilter({ ...filter, orderNo: value });
              }}
            />
          </InputContainer>
        </InputGroup>
      </InputGrid>

      <InputGroup style={{ width: '100%', marginBottom: '2rem' }}>
        <InputLabel>Address</InputLabel>
        <InputContainer style={{ width: '100%' }}>
          <Input
            placeholder='Enter Address'
            value={filter.address}
            onChange={(value) => {
              setFilter({ ...filter, address: value });
            }}
          />
        </InputContainer>
      </InputGroup>

      <SearchButton onClick={getDbInvoiceList}>SEARCH</SearchButton>

      {downloadError && <DownloadError>{downloadError}</DownloadError>}

      {invoiceList.length > 0 ? (
        <InvoiceListContainer>
          {invoiceList.map((invoice, index) => (
            <InvoiceListItem
              key={`${invoice.invoiceNumber || 'invoice'}-${index}`}
            >
              <Section>
                <InvoiceNumber>
                  Invoice # {invoice.invoiceNumber || '-'}
                </InvoiceNumber>
                <MutedText>
                  Invoice Date: {formatDate(invoice.invoiceDate)}
                </MutedText>
                <MutedText>Due Date: {formatDate(invoice.dueDate)}</MutedText>
              </Section>

              <Section>
                <MutedText>
                  <strong>Cust PO #:</strong>{' '}
                  {invoice.purchaseOrderNumber || '-'}
                </MutedText>
                <MutedText>
                  <strong>Address:</strong> {invoice.address || '-'}
                </MutedText>
              </Section>

              <AmountSection>
                <AmountText>
                  {formatAmount(invoice.totalAmount, invoice.currencyCode)}
                </AmountText>
                <DownloadButton
                  type='button'
                  disabled={
                    downloadingInvoice === String(invoice.invoiceNumber)
                  }
                  onClick={() => handleDownload(String(invoice.invoiceNumber))}
                >
                  <FaDownload aria-hidden='true' />
                  {downloadingInvoice === String(invoice.invoiceNumber)
                    ? 'DOWNLOADING...'
                    : 'DOWNLOAD PDF'}
                </DownloadButton>
              </AmountSection>
            </InvoiceListItem>
          ))}
        </InvoiceListContainer>
      ) : (
        <NoInvoicesMessage>No invoices</NoInvoicesMessage>
      )}
    </>
  );
};

export default PreviousInvoiceView;
